/* =========================================
   GIFTHOME LIFESTYLE GALLERY
========================================= */

const ghLifestyleGalleryCleanup = new Map();

function initGiftHomeLifestyleGalleries(root = document) {
  ghLifestyleGalleryCleanup.forEach((cleanup, gallery) => {
    if (!gallery.isConnected) {
      cleanup();
      ghLifestyleGalleryCleanup.delete(gallery);
    }
  });

  root.querySelectorAll('.gh-lifestyle-gallery').forEach((gallery) => {
    if (gallery.dataset.ghGalleryReady === 'true') return;
    const track = gallery.querySelector('.gh-lifestyle-gallery__track');
    const slides = Array.from(gallery.querySelectorAll('.gh-lifestyle-gallery__slide'));
    if (!track || slides.length < 2) return;
    gallery.dataset.ghGalleryReady = 'true';

    const controller = new AbortController();
    const signal = controller.signal;
    const prev = gallery.querySelector('.gh-lifestyle-gallery__arrow--prev');
    const next = gallery.querySelector('.gh-lifestyle-gallery__arrow--next');
    const dots = Array.from(gallery.querySelectorAll('.gh-lifestyle-gallery__dot'));
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const turkish = document.documentElement.lang.startsWith('tr');
    let current = 0;
    let timer = null;
    let scrollTimer = null;
    let visible = !('IntersectionObserver' in window);
    let hovering = false;
    let dragging = false;
    let scrolling = false;
    let userPaused = motion.matches;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'gh-lifestyle-gallery__autoplay';
    toggle.style.cssText = 'display:block;margin:12px auto 0;padding:8px 14px;min-height:40px;border:1px solid #b89547;border-radius:20px;background:#111;color:#e2c66d;font:inherit;font-size:12px;cursor:pointer';
    gallery.appendChild(toggle);

    function updateControl() {
      toggle.textContent = userPaused
        ? (turkish ? 'Otomatik geçişi başlat' : 'Start slideshow')
        : (turkish ? 'Otomatik geçişi durdur' : 'Pause slideshow');
    }

    function updateDots() {
      dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === current);
        if (index === current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    function schedule() {
      clearTimeout(timer);
      timer = null;
      if (!gallery.isConnected || document.hidden || !visible || userPaused || hovering || dragging || scrolling || gallery.contains(document.activeElement)) return;
      timer = setTimeout(() => goTo(current + 1), 2000);
    }

    function goTo(index) {
      current = (index + slides.length) % slides.length;
      track.scrollTo({
        left: slides[current].offsetLeft - slides[0].offsetLeft,
        behavior: motion.matches ? 'auto' : 'smooth'
      });
      updateDots();
      schedule();
    }

    prev?.addEventListener('click', (event) => {
      event.preventDefault();
      goTo(current - 1);
    }, { signal });
    next?.addEventListener('click', (event) => {
      event.preventDefault();
      goTo(current + 1);
    }, { signal });
    dots.forEach((dot, index) => dot.addEventListener('click', (event) => {
      event.preventDefault();
      goTo(index);
    }, { signal }));

    toggle.addEventListener('click', () => {
      userPaused = !userPaused;
      updateControl();
      schedule();
    }, { signal });
    gallery.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      hovering = true;
      schedule();
    }, { signal });
    gallery.addEventListener('pointerleave', () => {
      hovering = false;
      schedule();
    }, { signal });
    track.addEventListener('pointerdown', () => {
      dragging = true;
      schedule();
    }, { signal, passive: true });
    const stopDrag = () => { dragging = false; schedule(); };
    window.addEventListener('pointerup', stopDrag, { signal });
    window.addEventListener('pointercancel', stopDrag, { signal });
    gallery.addEventListener('focusin', schedule, { signal });
    gallery.addEventListener('focusout', () => queueMicrotask(schedule), { signal });
    document.addEventListener('visibilitychange', schedule, { signal });
    motion.addEventListener('change', () => {
      if (motion.matches) userPaused = true;
      updateControl();
      schedule();
    }, { signal });

    track.addEventListener('scroll', () => {
      scrolling = true;
      schedule();
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        let nearest = 0;
        let distance = Infinity;
        slides.forEach((slide, index) => {
          const delta = Math.abs(slide.offsetLeft - slides[0].offsetLeft - track.scrollLeft);
          if (delta < distance) { distance = delta; nearest = index; }
        });
        current = nearest;
        scrolling = false;
        updateDots();
        schedule();
      }, 100);
    }, { signal, passive: true });

    let observer;
    if ('IntersectionObserver' in window) {
      observer = new window.IntersectionObserver((entries) => {
        visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.2;
        schedule();
      }, { threshold: [0, 0.2] });
      observer.observe(gallery);
    }

    ghLifestyleGalleryCleanup.set(gallery, () => {
      controller.abort();
      clearTimeout(timer);
      clearTimeout(scrollTimer);
      observer?.disconnect();
    });
    updateControl();
    updateDots();
    schedule();
  });
}


/* =========================================
   GIFTHOME PRODUCT VIDEO AUTOPLAY / RESUME
========================================= */

function initGiftHomeShowcaseVideos(root = document) {
  root.querySelectorAll('.gh-product-showcase__video').forEach((video) => {
    if (video.dataset.ghVideoReady === 'true') return;
    video.dataset.ghVideoReady = 'true';

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.autoplay = false;
    video.playsInline = true;

    let sufficientlyVisible = false;

    const syncPlayback = () => {
      if (!sufficientlyVisible || document.hidden) {
        video.pause();
        return;
      }

      video.muted = true;
      const playPromise = video.play();
      if (playPromise !== undefined) playPromise.catch(() => {});
    };

    video.ghSyncPlayback = syncPlayback;
    video.addEventListener('canplay', syncPlayback);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        sufficientlyVisible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
        syncPlayback();
      });
    }, { threshold: [0, 0.2, 0.5, 1] });

    syncPlayback();
    observer.observe(video);
  });
}

/* =========================================
   GIFTHOME CART GIFT OPTIONS
========================================= */

if (!customElements.get('cart-gift-options')) {
  const ghGiftState = {
    value: null,
    saved: '',
    timer: null,
    pending: null,
    checkoutPending: false,
    resumeForm: null,
    error: '',

    signature(value) {
      return JSON.stringify(value);
    },

    components() {
      return document.querySelectorAll('cart-gift-options');
    },

    sync() {
      this.components().forEach((component) => component.renderGiftState());
    },

    changed(value) {
      this.value = value;
      this.error = '';
      this.sync();
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.flush().catch(() => {});
      }, 500);
    },

    needsSave() {
      return this.value !== null && this.signature(this.value) !== this.saved;
    },

    async flush() {
      clearTimeout(this.timer);
      if (this.pending) return this.pending;
      if (!this.needsSave()) return;

      this.error = '';
      this.sync();
      this.pending = (async () => {
        try {
          // Serialize updates so an older request cannot overwrite newer text.
          while (this.needsSave()) {
            const snapshot = { ...this.value };
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 15000);
            try {
              const root = window.Shopify?.routes?.root || '/';
              const response = await fetch(`${root}cart/update.js`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json'
                },
                signal: controller.signal,
                body: JSON.stringify({ attributes: snapshot })
              });
              if (!response.ok) throw new Error('Gift options could not be saved');
              const cart = await response.json();
              for (const key of ['Gift Wrap', 'Gift Note']) {
                if ((cart.attributes?.[key] || '') !== snapshot[key]) {
                  throw new Error('Gift options were not confirmed');
                }
              }
              this.saved = this.signature(snapshot);
            } finally {
              clearTimeout(timeout);
            }
          }
        } catch (error) {
          this.error = document.documentElement.lang.startsWith('tr')
            ? 'Hediye bilgileriniz kaydedilemedi. İnternet bağlantınızı kontrol edip ödeme düğmesine yeniden basın.'
            : 'Your gift details could not be saved. Check your connection and try checkout again.';
          this.sync();
          throw error;
        } finally {
          this.pending = null;
        }
      })();
      return this.pending;
    }
  };

  class CartGiftOptions extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('input[type="checkbox"]');
      this.noteWrap = this.querySelector('.gh-gift-options__note');
      this.note = this.querySelector('textarea');
      if (!this.checkbox || !this.note) return;

      if (ghGiftState.value === null) {
        ghGiftState.value = {
          'Gift Wrap': this.checkbox.checked ? 'Yes' : '',
          'Gift Note': this.checkbox.checked ? this.note.value : ''
        };
        ghGiftState.saved = ghGiftState.signature(ghGiftState.value);
      }

      this.errorElement = this.querySelector('[data-gh-gift-error]');
      if (!this.errorElement) {
        this.errorElement = document.createElement('p');
        this.errorElement.setAttribute('data-gh-gift-error', '');
        this.errorElement.setAttribute('role', 'alert');
        this.errorElement.style.color = '#b42318';
        this.appendChild(this.errorElement);
      }

      this.onGiftChange = () => {
        ghGiftState.changed({
          'Gift Wrap': this.checkbox.checked ? 'Yes' : '',
          'Gift Note': this.checkbox.checked ? this.note.value : ''
        });
      };
      this.checkbox.addEventListener('change', this.onGiftChange);
      this.note.addEventListener('input', this.onGiftChange);
      this.renderGiftState();
    }

    disconnectedCallback() {
      this.checkbox?.removeEventListener('change', this.onGiftChange);
      this.note?.removeEventListener('input', this.onGiftChange);
    }

    renderGiftState() {
      if (!this.checkbox || !this.note || !ghGiftState.value) return;
      this.checkbox.checked = ghGiftState.value['Gift Wrap'] === 'Yes';
      if (this.note.value !== ghGiftState.value['Gift Note']) {
        this.note.value = ghGiftState.value['Gift Note'];
      }
      if (this.noteWrap) this.noteWrap.hidden = !this.checkbox.checked;
      if (this.errorElement) {
        this.errorElement.textContent = ghGiftState.error;
        this.errorElement.hidden = !ghGiftState.error;
      }
    }
  }

  customElements.define('cart-gift-options', CartGiftOptions);

  // Normal cart checkout must wait for the latest gift attributes.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    const submitter = event.submitter;
    if (!['cart', 'CartDrawer-Form'].includes(form.id)) return;
    if (submitter?.name !== 'checkout') return;
    if (ghGiftState.resumeForm === form) return;
    if (!ghGiftState.needsSave() && !ghGiftState.pending && !ghGiftState.checkoutPending) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (ghGiftState.checkoutPending) return;
    ghGiftState.checkoutPending = true;
    submitter.setAttribute('aria-busy', 'true');
    try {
      await ghGiftState.flush();
      const currentForm = document.getElementById(form.id);
      const currentButton = submitter.isConnected && submitter.form === currentForm
        ? submitter
        : Array.from(document.querySelectorAll('button[name="checkout"]'))
          .find((button) => button.form === currentForm);
      if (!currentForm || !currentButton || currentButton.disabled) {
        throw new Error('Checkout is no longer available');
      }
      ghGiftState.resumeForm = currentForm;
      try {
        currentForm.requestSubmit(currentButton);
      } finally {
        ghGiftState.resumeForm = null;
      }
    } catch (error) {
      if (!ghGiftState.error) {
        ghGiftState.error = document.documentElement.lang.startsWith('tr')
          ? 'Ödemeye geçilemedi. Lütfen yeniden deneyin.'
          : 'Checkout could not continue. Please try again.';
        ghGiftState.sync();
      }
    } finally {
      ghGiftState.checkoutPending = false;
      submitter.removeAttribute('aria-busy');
    }
  }, true);
}


/* =========================================
   INITIAL LOAD
========================================= */

function initGiftHomeComponents(root = document) {
  initGiftHomeLifestyleGalleries(root);
  initGiftHomeShowcaseVideos(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initGiftHomeComponents();
  });
} else {
  initGiftHomeComponents();
}


/* =========================================
   SHOPIFY THEME EDITOR
========================================= */

document.addEventListener('shopify:section:load', (event) => {
  initGiftHomeComponents(event.target);
});


/* =========================================
   VIDEO RESUME ON TAB RETURN
========================================= */

document.addEventListener('visibilitychange', () => {
  document
    .querySelectorAll('.gh-product-showcase__video')
    .forEach((video) => {
      if (typeof video.ghSyncPlayback === 'function') {
        video.ghSyncPlayback();
      }
    });
});


/* =========================================
   DYNAMIC DOM SUPPORT
========================================= */

const ghVideoMutationObserver = new MutationObserver(() => {
  initGiftHomeLifestyleGalleries();
  initGiftHomeShowcaseVideos();
});

ghVideoMutationObserver.observe(document.body, {
  childList: true,
  subtree: true
});